import { useRef, useState } from "react";
import { recusaDoDocumento, tamanhoLegivel, tipoDoArquivo } from "../lib/perito/arquivos";
import { formatarDataHora } from "../lib/perito/datas";
import { usePerito } from "../lib/perito/store";
import type { Documento } from "../lib/perito/tipos";
import { BOTAO_PERIGO, BOTAO_SECUNDARIO, Dialogo, IconeAbrirFora, IconeBaixar, IconeDocumento, IconeEnviar, IconeLixo } from "./PeritoUi";

// Os documentos de uma perícia (fase C) — o despacho, o laudo, as petições e as guias. Portado de
// `src/components/pericias/DocumentosPericia.tsx` da Central do Perito, no commit f5c1923.
//
// Os arquivos ficam no bucket privado `perito-documentos` (000385), na pasta do dono e da perícia, e quem garante
// que cada aluno abre só os seus são as políticas do bucket, não esta tela. O tipo e o tamanho são conferidos
// AQUI, antes de subir: o bucket recusaria do mesmo jeito, com uma frase em inglês que não diz o que fazer.
//
// O que muda de lá: excluir pede confirmação num diálogo da casa (lá era o `confirm()` do navegador), e a falha
// de um arquivo fica escrita na tela, com o nome dele, em vez de um aviso que some sozinho.

/**
 * Abre ou baixa um documento por um endereço assinado de 5 minutos. A janela abre ANTES de buscar o endereço:
 * aberta depois de um `await`, ela deixa de contar como resposta ao clique e o bloqueador de pop-up a barra.
 */
async function abrir(doc: Documento, baixar: boolean): Promise<boolean> {
  const janela = baixar ? null : window.open("about:blank", "_blank");
  try {
    const url = await usePerito.getState().enderecoDoDocumento(doc, baixar);
    if (janela) {
      janela.opener = null;
      janela.location.href = url;
    } else {
      const link = document.createElement("a");
      link.href = url;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    return true;
  } catch (e) {
    janela?.close();
    console.error("[perito] o documento não abriu:", e);
    return false;
  }
}

type Recado = { tom: "erro" | "ok"; texto: string };

export function DocumentosDaPericia({ periciaId, escrever }: { periciaId: string; escrever: boolean }) {
  const s = usePerito();
  const escolher = useRef<HTMLInputElement>(null);
  const [arrastando, setArrastando] = useState(false);
  const [enviando, setEnviando] = useState<string[]>([]);
  const [recados, setRecados] = useState<Recado[]>([]);
  const [excluindo, setExcluindo] = useState<Documento | null>(null);
  const documentos = s.documentos.filter((d) => d.pericia_id === periciaId).sort((a, b) => b.criadoEm.localeCompare(a.criadoEm));

  const enviar = async (lista: FileList | null) => {
    if (!lista || lista.length === 0) return;
    const novos: Recado[] = [];
    for (const arquivo of Array.from(lista)) {
      const recusa = recusaDoDocumento(arquivo.name, arquivo.type, arquivo.size);
      const tipo = tipoDoArquivo(arquivo.name, arquivo.type);
      if (recusa || !tipo) {
        novos.push({ tom: "erro", texto: `"${arquivo.name}" não foi enviado. ${recusa ?? ""}`.trim() });
        continue;
      }
      setEnviando((e) => [...e, arquivo.name]);
      try {
        await s.anexarDocumento(periciaId, arquivo, arquivo.name, tipo);
        novos.push({ tom: "ok", texto: `"${arquivo.name}" foi guardado.` });
      } catch (e) {
        console.error("[perito] o envio falhou:", e);
        novos.push({ tom: "erro", texto: `Não foi possível enviar "${arquivo.name}". Tente de novo.` });
      } finally {
        setEnviando((e) => e.filter((n) => n !== arquivo.name));
      }
    }
    setRecados(novos);
  };

  const abrirOuBaixar = async (doc: Documento, baixar: boolean) => {
    const deu = await abrir(doc, baixar);
    if (!deu) setRecados([{ tom: "erro", texto: `Não foi possível ${baixar ? "baixar" : "abrir"} "${doc.nome}". Tente de novo.` }]);
  };

  const confirmarExclusao = () => {
    if (!excluindo) return;
    s.removerDocumento(excluindo.id);
    setRecados([{ tom: "ok", texto: `"${excluindo.nome}" foi excluído.` }]);
    setExcluindo(null);
  };

  return (
    <div className="flex flex-col gap-3" data-documentos>
      {escrever && (
        <>
          {/* Um <button> de verdade, e não uma <div> com onClick: a área de soltar arquivo também abre pelo teclado. */}
          <button type="button" data-anexar
            onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => { e.preventDefault(); setArrastando(false); void enviar(e.dataTransfer.files); }}
            onClick={() => escolher.current?.click()}
            className={`flex w-full flex-col items-center gap-2 rounded-cf-lg border-2 border-dashed p-6 text-center transition ${arrastando ? "border-laranja bg-alerta-fundo" : "border-borda hover:border-acento"}`}>
            <span className="grid size-11 place-items-center rounded-xl bg-tile text-laranja-texto"><IconeEnviar className="size-5" /></span>
            <span className="font-display text-sm font-bold text-titulo">Arraste arquivos ou clique para anexar</span>
            <span className="text-xs text-cinza">O despacho, o laudo, as petições e as guias. PDF, imagem, Word, Excel, TXT e CSV, até 20 MB cada.</span>
          </button>
          <input ref={escolher} type="file" multiple className="hidden" data-arquivos-documento
            onChange={(e) => {
              const arquivos = e.target.files;
              void enviar(arquivos).finally(() => { if (escolher.current) escolher.current.value = ""; });
            }} />
        </>
      )}

      {recados.length > 0 && (
        <ul className="flex flex-col gap-1.5" data-recados>
          {recados.map((r, i) => (
            <li key={i} role={r.tom === "erro" ? "alert" : "status"} data-recado={r.tom}
              className={`rounded-xl p-3 text-sm ${r.tom === "erro" ? "bg-erro-fundo text-erro-tinta" : "bg-aviso-fundo text-aviso-tinta"}`}>
              {r.texto}
            </li>
          ))}
        </ul>
      )}

      {enviando.map((nome) => (
        <div key={`enviando-${nome}`} role="status" className="flex items-center gap-3 rounded-xl border border-borda bg-fundo px-3 py-2.5 text-sm text-cinza" data-enviando>
          <svg viewBox="0 0 24 24" className="size-4 shrink-0 text-laranja-texto motion-safe:animate-spin" aria-hidden="true">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="14 43" strokeLinecap="round" />
          </svg>
          <span className="truncate">Enviando {nome}…</span>
        </div>
      ))}

      {documentos.length === 0 && enviando.length === 0 && (
        <p className="py-3 text-center text-sm text-cinza" data-sem-documentos>Nenhum documento guardado nesta perícia.</p>
      )}

      {documentos.length > 0 && (
        <ul className="flex flex-col gap-2">
          {documentos.map((d) => (
            <li key={d.id} className="flex items-center gap-3 rounded-xl border border-borda bg-superficie px-3 py-2.5" data-documento={d.id}>
              <span className="grid size-9 shrink-0 place-items-center rounded-[11px] bg-tile text-link"><IconeDocumento /></span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-titulo" data-documento-nome>{d.nome}</div>
                <div className="text-xs text-cinza tabular-nums">{tamanhoLegivel(d.tamanho)} · {formatarDataHora(d.criadoEm)}</div>
              </div>
              <button type="button" title="Abrir" aria-label={`Abrir ${d.nome}`} onClick={() => void abrirOuBaixar(d, false)} data-abrir-documento
                className="grid size-10 shrink-0 place-items-center rounded-full text-cinza hover:bg-chip hover:text-titulo"><IconeAbrirFora /></button>
              <button type="button" title="Baixar" aria-label={`Baixar ${d.nome}`} onClick={() => void abrirOuBaixar(d, true)} data-baixar-documento
                className="grid size-10 shrink-0 place-items-center rounded-full text-cinza hover:bg-chip hover:text-titulo"><IconeBaixar /></button>
              {escrever && (
                <button type="button" title="Excluir" aria-label={`Excluir ${d.nome}`} onClick={() => setExcluindo(d)} data-excluir-documento
                  className="grid size-10 shrink-0 place-items-center rounded-full text-cinza hover:bg-chip hover:text-erro-tinta"><IconeLixo /></button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialogo aberto={!!excluindo} aoFechar={() => setExcluindo(null)} titulo="Excluir este documento?"
        rodape={<>
          <button type="button" className={BOTAO_SECUNDARIO} onClick={() => setExcluindo(null)}>Cancelar</button>
          <button type="button" className={BOTAO_PERIGO} onClick={confirmarExclusao} data-confirmar-exclusao-documento>Excluir o documento</button>
        </>}>
        <p className="text-sm leading-relaxed text-tinta">
          <span className="font-semibold">{excluindo?.nome}</span> sai do armazenamento, e não há como recuperar o arquivo.
        </p>
      </Dialogo>
    </div>
  );
}
